<script lang="ts">
import {ref, defineComponent, onMounted, watch, computed} from 'vue';
import {useAuthStore} from '~/plugins/store/auth';
import {useTranslationStore} from '~/plugins/store/translations';
import {useVehicleStore} from '~/plugins/store/vehicle';
import {useNuxtApp} from '#app';
import {
  type ApiTravelDetail,
  createDefaultTravelForm,
  createDefaultValidationTravelStatus,
  type TravelForm,
  type TravelInsuranceCompany,
  type ValidationTravelStatus,
  type AmountInsured,
  type TravelOffer as TravelOfferObj,
  type TravelUsersFormValidation,
  type TravelUserFormValidation,
  createDefaultTravelUserFormValidation,
} from '~/types/Travel';
import {translation} from '~/plugins/store/translations';
import {type ApiVehicleDetails, type BasicVehicle, createDefaultBasicVehicle} from '~/types/Vehicle';
import MasterLayout from "~/layouts/MasterLayout.vue";
import VehicleSelect from "~/components/vehicle/VehicleSelect.vue";
import AddBasicVehicleDialog from "~/components/vehicle/AddBasicVehicleDialog.vue";
import TravelCheckboxRoadAssistance from "~/components/travel/TravelCheckboxRoadAssistance.vue";
import CustomerDocumentTypeSelect from "~/components/customer/CustomerDocumentTypeSelect.vue";
import TravelTransportationMethodSelect from "~/components/travel/TravelTransportationMethodSelect.vue";
import TravelNumberOfDays from "~/components/travel/TravelNumberOfDays.vue";
import type {TranslationList} from "~/types/Utils";
import {type CustomerForm, createDefaultCustomerForm, type RcaDriverFormValidation} from "~/types/Customer";
import {useProductStore} from "~/plugins/store/product";
import {useTravelStore} from "~/plugins/store/travel";
import {recaptcha} from "~/composables/recaptcha";
import {useReCaptcha} from "vue-recaptcha-v3";
import ConfirmationDialog from "~/components/utils/ConfirmationDialog.vue";
import TravelOffer from "~/components/travel/TravelOffer.vue";
import LoadingDialog from "~/components/utils/LoadingDialog.vue";
import {type AddressFormValidation, createDefaultAddress} from "~/types/Address";
import type {Product} from "~/types/Product";

export default defineComponent({
  setup() {
    useHead({
      title: '24Broker | Asigurare de calatorie',
      meta: [
        {
          name: 'description',
          content: 'Găsește cea mai bună asigurare de călătorie pentru vacanța ta! Protejează-te în timpul călătoriilor internaționale cu polițe personalizate, adaptate nevoilor tale.'
        }
      ],
    });

    // Utility function to introduce a delay
    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const authStore = useAuthStore();
    const user = ref(authStore.getUser());
    let accessToken = authStore.getAccessToken();
    const {$toast} = useNuxtApp();
    const translationStore = useTranslationStore();
    const translationList = ref<TranslationList>({});
    const travelForm = ref<TravelForm>(createDefaultTravelForm());
    const {$dayjs} = useNuxtApp();
    const productStore = useProductStore();
    let travelProduct = <Product>({});
    const validationTravelStatus = ref<ValidationTravelStatus>(createDefaultValidationTravelStatus());
    const validationTravelUsersStatus = ref<TravelUsersFormValidation>({});
    const userAlreadyExistsWithEmail = ref(false);
    const isPersonalVehicleSelected = ref(false);
    const vehicles = ref<BasicVehicle[]>([]);
    const storeVehicle = useVehicleStore();
    const showAddBasicVehicleDialog = ref(false);
    const travelStore = useTravelStore();
    const travelDetail = ref<ApiTravelDetail>();
    const insuranceCompanies = ref<TravelInsuranceCompany[]>([]);
    const amountInsuredList = ref<AmountInsured[]>([]);
    const offers = ref<TravelOfferObj[][]>([]);
    const tab = ref("2");
    const showLoadingDialog = ref(false);
    const messageLoadingDialog = ref('');
    const amountKeyList = ref(travelStore.amountKeyList);
    const recaptchaInstance = useReCaptcha();
    const activeStep = ref(1);
    const showConfirmationDialog = ref(false);
    const messageConfirmationDialog = ref('');
    const stepperItems = ref(
        [
          {
            title: translation(translationList.value.trip_details, 'Detalii călătorie'),
            rules: [() => false],
          },
          {
            title: translation(translationList.value.comparator, 'Comparator'),
          }
        ]);
    const activePanels = ref([0]);
    const isLoadingSkeleton = ref(false);
    const isOffersMailSent = ref(false);

    const SNAPSHOT_KEY = 'travel_form_snapshot';

    const backToStep = (step: number) => {
      //reset offers
      offers.value = [];
      activeStep.value = step;
    };

    // Functie pentru salvarea snapshot-ului
    const saveSnapshot = async (): Promise<void> => {
      // Introduce a delay of 1.5 second (1500 milliseconds)
      await delay(1500);
      const snapshot = {
        travelForm: travelForm.value,
        validationTravelStatus: validationTravelStatus.value,
        validationTravelUsersStatus: validationTravelUsersStatus.value,
        user: user.value,
        isPersonalVehicleSelected: isPersonalVehicleSelected.value,
      };
      localStorage.setItem(SNAPSHOT_KEY, JSON.stringify(snapshot));
      $toast.info(translation(translationList.value.session_saved, 'Sesiunea a fost salvată!'));
    };
    const restoreSnapshot = async (): Promise<void> => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.snapshot_restoring, 'Restaurăm sesiunea anterioară...');
      // Introduce a delay of 1.5 second (1500 milliseconds)
      await delay(1500);
      const savedSnapshot = localStorage.getItem(SNAPSHOT_KEY);
      if (savedSnapshot) {
        const snapshot = JSON.parse(savedSnapshot);
        travelForm.value = snapshot.travelForm;
        validationTravelStatus.value = snapshot.validationTravelStatus;
        validationTravelUsersStatus.value = snapshot.validationTravelUsersStatus;
        if (authStore.getUser()) {
          user.value = snapshot.user;
        }
        isPersonalVehicleSelected.value = snapshot.isPersonalVehicleSelected;
        $toast.info(translation(translationList.value.session_restored, 'Sesiunea a fost restaurată!'));
      }
      showLoadingDialog.value = false;
    };

    const scrollToTop = async () => {
      const {$scrollToTop} = useNuxtApp();
      $scrollToTop();
    }

    onMounted(async () => {
      translationList.value = await translationStore.getTranslations('ro', 'dashboard');
      insuranceCompanies.value = await travelStore.getInsuranceCompanies();
      amountInsuredList.value = await travelStore.getAmountInsuredList();

      fillUserData();

      const savedSnapshot = localStorage.getItem(SNAPSHOT_KEY);
      if (savedSnapshot) {
        showConfirmationDialog.value = true;
        messageConfirmationDialog.value = translation(translationList.value.restore_session,
            'Restaurare sesiune anterioară');
      }
      if (!productStore.productStatuses.is_travel_available) {
        navigateTo('/product-unavailable');
      }

      travelProduct = await productStore.getTravelProduct();
    });

    const fillUserData = () => {
      if (user.value) {
        travelForm.value.email = user.value.email;
        validationTravelStatus.value.email = true;
        travelForm.value.phone_number = user.value.phone_number;
        validationTravelStatus.value.phone_number = true;
      }
    }

    const handleValidationTravelStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      validationTravelStatus.value[field] = isValid;
    };

    const handleValidationTravelUserStatus = ({field, isValid}: { field: string, isValid: boolean }) => {
      const [baseField, index] = field.split('-');

      if (index !== undefined) {
        const numericIndex = Number(index);
        if (isNaN(numericIndex)) {
          console.error('Invalid index:', index);
          return;
        }

        if (!validationTravelUsersStatus.value[numericIndex]) {
          validationTravelUsersStatus.value[numericIndex] = createDefaultTravelUserFormValidation();
        }

        const travelUserValidation = validationTravelUsersStatus.value[numericIndex] as TravelUserFormValidation;

        if (baseField === 'street'
            || baseField === 'street_number'
            || baseField === 'building'
            || baseField === 'entrance'
            || baseField === 'apartment'
            || baseField === 'floor'
            || baseField === 'country'
            || baseField === 'city'
            || baseField === 'postal_code'
            || baseField === 'county'
        ) {
          travelUserValidation.address[baseField] = isValid;
        } else if (baseField === 'series_ci') {
          travelUserValidation.series = isValid;
        } else if (baseField === 'nr_ci') {
          travelUserValidation.number = isValid;
        } else {
          travelUserValidation[baseField] = isValid;
        }
      }
    };

    const handleAutoCompleteAddress = (index: number) => {
      if (travelForm.value.travel_users.length > 1) {
        const previousUser = travelForm.value.travel_users[0];
        const currentTravelUser = travelForm.value.travel_users[index];
        if (currentTravelUser) {
          currentTravelUser.address = {...previousUser.address};
        }
      }
    };


    const handleUserLoggedIn = async () => {
      const newUser = authStore.getUser();
      $toast.success(translation(translationList.value.successfully_logged_in, 'Te-ai logat cu succes!'));
      accessToken = authStore.getAccessToken();
      userAlreadyExistsWithEmail.value = false;
      user.value = newUser;
      fillUserData();
    };

    const handleAddBasicVehicleDialogResponse = () => {
      showAddBasicVehicleDialog.value = false;
    };

    const handleConfirmationDialogResponse = async ({response, identifier}: {
      response: boolean,
      identifier: string
    }) => {
      if (response) {
        await restoreSnapshot();
      } else {
        localStorage.removeItem(SNAPSHOT_KEY);
      }
    };


    watch(() => travelForm.value.email, async (newValue, oldValue) => {
      if (newValue !== oldValue && !user.value && newValue && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newValue)) {
        const token = await recaptcha(recaptchaInstance);
        userAlreadyExistsWithEmail.value = newValue ? await authStore.checkEmailExists(newValue, token) : false;
      }
    });

    watch(() => travelForm.value.vehicle?.registration_plate, async (newValue, oldValue) => {
      if (newValue !== oldValue && newValue) {
        validationTravelStatus.value.vehicle.registration_plate = true;
        validationTravelStatus.value.vehicle.vin = true;
        validationTravelStatus.value.vehicle.make = true;
        validationTravelStatus.value.vehicle.model = true;
        validationTravelStatus.value.vehicle.manufacture_year = true;
        validationTravelStatus.value.vehicle.number_of_seats = true;
        validationTravelStatus.value.vehicle.max_weight = true;
      }
    });

    watch(() => travelForm.value.number_of_days, async (newValue, oldValue) => {
      if (newValue !== oldValue) {
        if (newValue) {
          if (travelForm.value.valid_from) {
            travelForm.value.valid_until = $dayjs(travelForm.value.valid_from).add(newValue, 'day').format('YYYY-MM-DD');
            validationTravelStatus.value.valid_until = true;
          }
        } else {
          travelForm.value.valid_until = null;
          validationTravelStatus.value.valid_until = false;
        }
      }

    });

    watch(() => travelForm.value.valid_from, async (newValue, oldValue) => {
      if (newValue !== oldValue) {
        if (newValue) {
          validationTravelStatus.value.valid_from = true;
          if (travelForm.value.number_of_days) {
            travelForm.value.valid_until = $dayjs(travelForm.value.valid_from).add(travelForm.value.number_of_days, 'day').format('YYYY-MM-DD');
            validationTravelStatus.value.valid_until = true;
          }
        } else {
          travelForm.value.valid_until = null;
          validationTravelStatus.value.valid_until = false;
          travelForm.value.valid_from = null;
          validationTravelStatus.value.valid_from = false;
        }
      }
    });

    watch(() => travelForm.value.transportation_method, async (newValue, oldValue) => {
      if (newValue !== oldValue && newValue) {
        isPersonalVehicleSelected.value = newValue.code === 'personal_car';
        if (!isPersonalVehicleSelected.value && travelForm.value.coverage_list) {
          travelForm.value.coverage_list.is_road_assistance_coverage_included = false;
        }
      }
    });

    const isRoadAssistanceIncluded = computed({
      get() {
        return travelForm.value.coverage_list?.is_road_assistance_coverage_included ?? false;
      },
      set(value: boolean) {
        if (travelForm.value.coverage_list) {
          travelForm.value.coverage_list.is_road_assistance_coverage_included = value;
        }
        if (!value) {
          travelForm.value.vehicle = createDefaultBasicVehicle();
        }
      },
    });

    watch(isRoadAssistanceIncluded, async (newValue, oldValue) => {
      if (newValue !== oldValue && newValue && !vehicles.value.length && accessToken) {
        await storeVehicle.fetchVehicles(accessToken);
        vehicles.value = storeVehicle.vehicles.map((vehicle: ApiVehicleDetails): BasicVehicle => ({
          vehicle_type: vehicle.vehicle_type,
          registration_plate: vehicle.registration_plate,
          vin: vehicle.vin,
          rca_mapped_category: vehicle.rca_mapped_category ?? null,
          make: vehicle.rca_vehicle_make ?? null,
          model: vehicle.rca_vehicle_model ?? null,
          manufacture_year: vehicle.manufacture_year ? parseInt(vehicle.manufacture_year, 10) : null,
          number_of_seats: vehicle.number_of_seats,
          max_weight: vehicle.max_weight
        }));
        if (!vehicles.value.length) {
          showAddBasicVehicleDialog.value = true;
        }
      }
    });

    const addTravelUser = () => {
      if (travelForm.value.number_of_travel_users < 5) {
        travelForm.value.number_of_travel_users += 1;

        const newTravelUser = {
          first_name: null,
          last_name: null,
          cnp: null,
          document_type: null,
          series: null,
          number: null,
          address: createDefaultAddress(),
        };
        travelForm.value.travel_users.push(newTravelUser);
      }
    };

    const removeTravelUser = () => {
      if (travelForm.value.number_of_travel_users > 1) {
        travelForm.value.number_of_travel_users -= 1;
        travelForm.value.travel_users.pop();
        delete validationTravelUsersStatus.value[travelForm.value.travel_users.length];
      }
    };

    const validateCNP = (cnp: string): boolean => {
      if (cnp.length !== 13) return false;

      const firstDigit = parseInt(cnp[0]);
      const year = parseInt(cnp.substring(1, 3));
      const month = parseInt(cnp.substring(3, 5));
      const day = parseInt(cnp.substring(5, 7));

      let fullYear;
      if (firstDigit === 1 || firstDigit === 2) {
        fullYear = 1900 + year;
      } else if (firstDigit === 3 || firstDigit === 4) {
        fullYear = 1800 + year;
      } else if (firstDigit === 5 || firstDigit === 6) {
        fullYear = 2000 + year;
      } else {
        return false; // Invalid CNP for non-residents
      }

      const birthDate = new Date(fullYear, month - 1, day);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      const dayDiff = today.getDate() - birthDate.getDate();

      if (monthDiff < 0 || (monthDiff === 0 && dayDiff < 0)) {
        age--;
      }

      return age >= 18;
    };

    const validateStep = async (step: number) => {
      let isFormValid = true;

      switch (step) {
        case 1:
          await scrollToTop();
          showLoadingDialog.value = true;
          messageLoadingDialog.value = translation(translationList.value.travel_form_validating, 'Validăm datele introduse...');

          const firstTravelUser = travelForm.value.travel_users[0];
          if (firstTravelUser.cnp && !validateCNP(firstTravelUser.cnp)) {
            $toast.error(translation(translationList.value.cnp_legal_age, 'CNP-ul introdus pentru primul călător nu este valid sau persoana nu are minim 18 ani!'));
            showLoadingDialog.value = false;
            return;
          }

          // Validate travel users using validationTravelUsersStatus
          for (const [index, userValidation] of Object.entries(validationTravelUsersStatus.value)) {
            if (userValidation) {
              const travelUserValidation = userValidation as TravelUserFormValidation;
              const travelUser = travelForm.value.travel_users[index];

              for (const [field, isValid] of Object.entries(travelUserValidation)) {
                if (field === 'address') {
                  const addressValidation = travelUserValidation.address as AddressFormValidation;
                  for (const [addressField, addressIsValid] of Object.entries(addressValidation)) {
                    if (['country', 'floor', 'entrance', 'apartment', 'building'].includes(addressField)) {
                      continue; // Skip validation for non-required fields
                    }
                    if (!addressIsValid) {
                      console.log(`Validation failed for field address: ${field}-${index}-${addressField}`);
                      window.dispatchEvent(new CustomEvent('validate-address', {detail: `${addressField}-${index}`}));
                      isFormValid = false;
                    }
                  }
                } else if (field === 'series' && travelUser.document_type && travelUser.document_type.code === 'passport') {
                  continue; // Skip validation for series
                } else if (!isValid) {
                  console.log(`Validation failed for field travel user: ${field}-${index}`);
                  window.dispatchEvent(new CustomEvent('validate-travel', {detail: field}));
                  isFormValid = false;
                }
              }
            }
          }

          // Validate travel form fields using validationTravelStatus
          for (const [field, isValid] of Object.entries(validationTravelStatus.value)) {
            if (field.startsWith('vehicle')) {
              if (isRoadAssistanceIncluded.value) {
                if (!travelForm.value.vehicle || !travelForm.value.vehicle.registration_plate) {
                  console.log(`Vehicle Validation failed for field vehicle: ${field}`);
                  window.dispatchEvent(new CustomEvent('validate-vehicle', {detail: field}));
                  isFormValid = false;
                  continue;
                }
              }
              continue;
            }
            if (typeof isValid === 'boolean' && !isValid) {
              isFormValid = false;
              console.log(`Validation failed for field travel normal: ${field}`);
              window.dispatchEvent(new CustomEvent('validate-travel', {detail: field}));
            } else if (Array.isArray(isValid)) {
              for (const [subField, subIsValid] of Object.entries(isValid)) {
                if (!subIsValid) {
                  console.log(`Validation failed for field travel normal: ${field}.${subField}`);
                  window.dispatchEvent(new CustomEvent('validate-travel', {detail: field}));
                  isFormValid = false;
                }
              }
            }
          }

          if (!isFormValid) {
            $toast.error(translation(translationList.value.fill_all_fields, 'Te rugăm să completezi toate câmpurile obligatorii corect!'));
            showLoadingDialog.value = false;
            return;
          }

          messageLoadingDialog.value = translation(translationList.value.snapshot_saving, 'Salvăm sesiunea...');
          await saveSnapshot();

          //if there is no user logged in with the email, create a new user
          if (!user.value) {
            const customerForm: CustomerForm = {...createDefaultCustomerForm};
            let travelUser = travelForm.value.travel_users[0];
            customerForm.first_name = travelUser.first_name;
            customerForm.last_name = travelUser.last_name;
            customerForm.email = travelForm.value.email;
            customerForm.phone_number = travelForm.value.phone_number;
            customerForm.cnp = travelUser.cnp;
            const token = await recaptcha(recaptchaInstance);
            const tmpAuthResponse = await authStore.storeUserFromProductPage(customerForm, travelProduct.id, token);
            if (tmpAuthResponse) {
              accessToken = tmpAuthResponse.access_token;
              userAlreadyExistsWithEmail.value = false;
              user.value = tmpAuthResponse.user;
            }
          }
          if (accessToken) {
            messageLoadingDialog.value = translation(translationList.value.offers_fetching, 'Obținem ofertele...');
            if (!await retrieveOffers()) {
              $toast.error(translation(translationList.value.error_retry, 'A apărut o eroare. Te rugăm să încerci din nou.'));
              showLoadingDialog.value = false;
              return;
            }
          } else {
            //if no accessToken is available, show an error message, don't proceed
            $toast.error(translation(translationList.value.error_retry, 'A apărut o eroare. Te rugăm să încerci din nou.'));
            showLoadingDialog.value = false;
            return;
          }

          activeStep.value = 2;
          showLoadingDialog.value = false;
          break;
      }
    };

    const retrieveOffers = async () => {
      if (!accessToken) {
        return false;
      }
      //const token = await recaptcha(recaptchaInstance); //todo uncomment
      const token = undefined; //todo: remove
      travelDetail.value = await travelStore.storeTravelDetail(travelForm.value, accessToken, token);
      if (travelDetail.value && travelDetail.value.id && insuranceCompanies.value) {
        const keys = Object.keys(amountInsuredList.value);
        for (let i = keys.length - 1; i >= 0; i--) {
          const key = parseInt(keys[i]);
          for (const secondKey in insuranceCompanies.value) {
            if (insuranceCompanies.value[secondKey]
                && insuranceCompanies.value[secondKey].id
                && amountInsuredList.value[key]
                && amountInsuredList.value[key].id
                && amountInsuredList.value[key].name
            ) {
              const token = await recaptcha(recaptchaInstance);
              travelStore.fetchOffers(travelDetail.value.id, <number>insuranceCompanies.value[secondKey].id, <number>amountInsuredList.value[key].id, accessToken, token)
                  .then((response: any) => {
                    if (response) {
                      const tmpOffers: any[] = [];
                      response.forEach((offer: any) => {
                        if (parseInt(offer.base_coverage_amount_insured) === parseInt(<string>amountInsuredList.value[key].name)) {
                          tmpOffers.push(offer);
                        }
                      });
                      console.log(tmpOffers);
                      if (offers.value[key]) {
                        offers.value[key] = [...offers.value[key], ...tmpOffers];
                      } else {
                        offers.value[key] = tmpOffers;
                      }
                    }
                  });
            }
          }
        }
      }
      return true;
    };

    const sendOffersToUser = async () => {
      showLoadingDialog.value = true;
      messageLoadingDialog.value = translation(translationList.value.sending_offers, 'Trimitem ofertele...');
      const token = await recaptcha(recaptchaInstance);
      const response = await travelStore.sendOffersToUser(
          offers.value,
          insuranceCompanies.value,
          <string>travelForm.value.email,
          accessToken,
          token
      );
      if (response) {
        isOffersMailSent.value = true;
        $toast.success(translation(translationList.value.offers_sent_success, 'Comparatorul a fost trimis pe email!'));
      } else {
        $toast.error(translation(translationList.value.error, 'A apărut o eroare.'));
        console.error('comparatorul nu a fost trimis pe email');
      }
      showLoadingDialog.value = false;
    };

    return {
      translationList,
      translation,
      travelForm,
      handleValidationTravelStatus,
      handleAutoCompleteAddress,
      showConfirmationDialog,
      userAlreadyExistsWithEmail,
      handleUserLoggedIn,
      handleAddBasicVehicleDialogResponse,
      handleConfirmationDialogResponse,
      user,
      isPersonalVehicleSelected,
      isRoadAssistanceIncluded,
      vehicles,
      showAddBasicVehicleDialog,
      validateStep,
      insuranceCompanies,
      offers,
      tab,
      showLoadingDialog,
      messageLoadingDialog,
      amountKeyList,
      activeStep,
      stepperItems,
      activePanels,
      messageConfirmationDialog,
      handleValidationTravelUserStatus,
      validationTravelUsersStatus,
      backToStep,
      isLoadingSkeleton,
      sendOffersToUser,
      isOffersMailSent,
      addTravelUser,
      removeTravelUser,
    };
  },
  components: {
    LoadingDialog,
    TravelOffer,
    ConfirmationDialog,
    VehicleSelect,
    AddBasicVehicleDialog,
    TravelCheckboxRoadAssistance,
    CustomerDocumentTypeSelect,
    TravelTransportationMethodSelect,
    TravelNumberOfDays,
    MasterLayout,
  }
});
</script>

<template>
  <master-layout class="travel">
    <v-container class="content-container background-gradient">
      <v-container fluid class="component-container-1400mw">
        <v-container class="centered-horizontally">
          <loading-dialog :message="messageLoadingDialog" :show-dialog="showLoadingDialog"/>
          <add-basic-vehicle-dialog
              :show-dialog="showAddBasicVehicleDialog"
              v-model="travelForm.vehicle"
              @response="handleAddBasicVehicleDialogResponse"
          >
          </add-basic-vehicle-dialog>
          <auth-already-exists-login-dialog
              :email="travelForm.email"
              :user-already-exists-with-email="userAlreadyExistsWithEmail"
              @loggedIn="handleUserLoggedIn"
          >
          </auth-already-exists-login-dialog>
          <confirmation-dialog
              :show-dialog="showConfirmationDialog"
              :message="messageConfirmationDialog"
              @response="handleConfirmationDialogResponse"
          ></confirmation-dialog>
          <v-stepper
              tile
              rounded="0"
              elevation="0"
              :items="stepperItems"
              class="travel-stepper"
              selected-class="active"
              v-model="activeStep"
              :hide-actions="true"
          >
            <template v-slot:item.1>
              <div>
                <v-row>
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.travel_destination, 'Destinație') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.travel_destination_helper, 'Indică locul în care aventura ta va prinde viață.')
                      }}
                    </p>
                  </v-col>
                </v-row>
                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="6" md="4">
                    <address-continent-select v-model="travelForm.continent"
                                              @validation-address-status="handleValidationTravelStatus">
                    </address-continent-select>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <address-country-select v-model="travelForm.country" :continent-code="travelForm.continent?.code"
                                            @validation-address-status="handleValidationTravelStatus">
                    </address-country-select>
                  </v-col>
                  <v-col cols="12" sm="12" md="4">
                    <travel-purpose-select v-model="travelForm.purpose"
                                           @validation-travel-status="handleValidationTravelStatus">
                    </travel-purpose-select>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.trip_period, 'Perioada de călătorie') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.trip_period_helper, 'Alege intervalul în care vei explora lumea.')
                      }}
                    </p>
                  </v-col>
                </v-row>
                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="6" md="4">
                    <travel-valid-from v-model="travelForm.valid_from"
                                       @validation-travel-status="handleValidationTravelStatus">
                    </travel-valid-from>
                  </v-col>
                  <v-col cols="12" sm="6" md="4">
                    <travel-number-of-days v-model="travelForm.number_of_days"
                                           @validation-travel-status="handleValidationTravelStatus">
                    </travel-number-of-days>
                  </v-col>
                  <v-col cols="12" sm="12" md="4">
                    <div v-show="travelForm.valid_until" class="mb-5">
                      {{ translation(translationList.valid_until, 'Valabil până la') }}: <b>{{
                        travelForm.valid_until
                      }}</b>
                    </div>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.transport, 'Transport') }}</h3>
                    <p class="font-weight-regular">
                      {{ translation(translationList.transport_helper, 'Spune-ne cum vei ajunge la destinație.') }}
                    </p>
                  </v-col>
                </v-row>
                <v-row class="d-flex align-center">
                  <v-col cols="12" sm="12" md="6">
                    <travel-transportation-method-select v-model="travelForm.transportation_method"
                                                         @validation-travel-status="handleValidationTravelStatus">
                    </travel-transportation-method-select>
                  </v-col>
                  <v-col v-if="isPersonalVehicleSelected" cols="12" sm="12" md="6">
                    <travel-checkbox-road-assistance v-model="isRoadAssistanceIncluded"
                                                     @validation-travel-status="handleValidationTravelStatus">
                    </travel-checkbox-road-assistance>
                  </v-col>
                </v-row>


                <v-row v-if="isRoadAssistanceIncluded">
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.traveler_vehicle, 'Vehiculul tău') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.traveler_vehicle_helper, 'Alege un vehicul din listă sau adaugă unul nou pentru asistență rutieră.')
                      }}
                    </p>
                  </v-col>
                </v-row>
                <v-row v-if="isRoadAssistanceIncluded">
                  <v-col cols="12" sm="12" md="6">
                    <vehicle-select :vehicles="vehicles" v-model="travelForm.vehicle" :canBeCleared="true">
                    </vehicle-select>
                  </v-col>
                  <v-col v-if="(!travelForm.vehicle || !travelForm.vehicle.registration_plate)" cols="12" sm="12"
                         md="6">
                    <v-btn
                        class="w-100"
                        min-width="0"
                        color="primary"
                        size="x-large"
                        flat
                        rounded="0"
                        @click="showAddBasicVehicleDialog = true">
                      {{ translation(translationList.add_vehicle, 'Adaugă vehicul') }}
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row v-if="!user">
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.travelers_info, 'Informații de contact') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.travelers_info_helper, 'Completează adresa de email și numărul de telefon pentru a te putea contacta.')
                      }}
                    </p>
                  </v-col>
                </v-row>
                <v-row v-if="!user" class="d-flex align-center">
                  <v-col cols="12" sm="12" md="6">
                    <customer-email v-model="travelForm.email"
                                    @validation-customer-status="handleValidationTravelStatus">
                    </customer-email>
                  </v-col>
                  <v-col cols="12" sm="12" md="6">
                    <customer-phone-number v-model="travelForm.phone_number"
                                           @validation-customer-status="handleValidationTravelStatus">

                    </customer-phone-number>
                  </v-col>
                </v-row>

                <v-row class="pb-5">
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.travelers_no, 'Număr călători') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.travelers_no_helper, 'Selectează câte persoane vor călători împreună.')
                      }}
                    </p>
                  </v-col>
                  <v-col cols="12" sm="12" md="6" class="d-flex align-center justify-end centered-on-mobile">
                    <v-btn
                        min-width="0"
                        size="small"
                        icon="mdi-minus"
                        color="primary-light"
                        variant="flat"
                        :disabled="travelForm.number_of_travel_users <= 1"
                        @click="removeTravelUser"
                    >
                    </v-btn>
                    <div class="mx-2 fw-500">
                      {{ travelForm.number_of_travel_users }}
                    </div>
                    <v-btn
                        min-width="0"
                        size="small"
                        icon="mdi-plus"
                        color="primary"
                        flat
                        :disabled="travelForm.number_of_travel_users >= 5"
                        @click="addTravelUser"
                    >
                    </v-btn>
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12" sm="12" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                    <h3 class="font-weight-bold">
                      {{ translation(translationList.travelers_info, 'Informații despre călători') }}</h3>
                    <p class="font-weight-regular">
                      {{
                        translation(translationList.travelers_info_helper, 'Completează detalii esențiale despre persoanele aflate în călătorie.')
                      }}
                    </p>
                  </v-col>
                </v-row>
                <v-expansion-panels color="primary-light" v-model="activePanels" rounded="0" variant="accordion"
                                    class="mt-3">
                  <v-expansion-panel eager class="w-100" v-for="(travelUser, index) in travelForm.travel_users"
                                     :key="index">
                    <v-expansion-panel-title>
                      <template v-slot:default="{ expanded }">
                        <v-row class="d-flex align-center" no-gutters>
                          <v-col class="d-flex justify-start my-1" cols="12" sm="3" md="2">
                            <h3 class="font-weight-bold text-primary">
                              {{ translation(translationList.travel_user, 'Călător') }} #{{ index + 1 }}</h3>
                          </v-col>
                          <v-col class="my-1" cols="12" sm="9" md="10">
                            <v-fade-transition leave-absolute>
                              <h4 v-if="!expanded" class="font-weight-bold text-contrast">
                                {{ travelUser.last_name }} {{ travelUser.first_name }} </h4>
                            </v-fade-transition>
                          </v-col>
                        </v-row>
                      </template>
                    </v-expansion-panel-title>
                    <v-expansion-panel-text>
                      <v-row>
                        <v-col cols="12" sm="auto" md="6"
                               class="d-flex flex-column justify-start centered-on-mobile">
                          <p class="font-weight-regular">
                            {{
                              translation(translationList.travel_user_helper, 'Completează datele personale și adresa conform documentului de identitate.')
                            }}
                          </p>
                        </v-col>
                      </v-row>
                      <v-row class="d-flex align-center">
                        <v-col cols="12" sm="12" md="4">
                          <customer-last-name v-model="travelUser.last_name"
                                              :customer-index="index"
                                              @validation-customer-status="handleValidationTravelUserStatus">
                          </customer-last-name>
                        </v-col>
                        <v-col cols="12" sm="12" md="4">
                          <customer-first-name v-model="travelUser.first_name"
                                               :customer-index="index"
                                               @validation-customer-status="handleValidationTravelUserStatus">
                          </customer-first-name>
                        </v-col>
                        <v-col cols="12" sm="12" md="4">
                          <customer-cnp v-model="travelUser.cnp"
                                        :customer-index="index"
                                        @validation-customer-status="handleValidationTravelUserStatus">
                          </customer-cnp>
                        </v-col>
                      </v-row>
                      <v-row class="d-flex align-center">
                        <v-col sm="12" md="4">
                          <customer-document-type-select v-model="travelUser.document_type"
                                                         :customer-index="index"
                                                         @validation-customer-status="handleValidationTravelUserStatus">
                          </customer-document-type-select>
                        </v-col>
                        <v-col cols="12" sm="12" md="4" v-show="travelUser.document_type?.code === 'CI'">
                          <customer-series-ci v-model="travelUser.series"
                                              :customer-index="index"
                                              @validation-customer-status="handleValidationTravelUserStatus">
                          </customer-series-ci>
                        </v-col>
                        <v-col cols="12" sm="12" md="4">
                          <customer-nr-ci v-model="travelUser.number"
                                          :customer-index="index"
                                          @validation-customer-status="handleValidationTravelUserStatus">
                          </customer-nr-ci>
                        </v-col>
                      </v-row>

                      <v-row v-if="index !== 0" class="d-flex align-center">
                        <v-col cols="12" sm="6" md="6"
                               class="d-flex flex-column justify-start centered-on-mobile">
                          <p class="font-weight-regular">
                            {{
                              translation(translationList.travel_user_copy_address, 'Dacă doriți să utilizați adresa primului călător, puteți completa automat cu un singur click.')
                            }}
                          </p>
                        </v-col>
                        <v-col cols="12" sm="6" md="6" class="d-flex flex-column justify-start centered-on-mobile">
                          <v-tooltip
                              location="top" :text="translation(translationList.travel_user_copy_address,
                          'Dacă doriți să utilizați adresa primului călător, puteți completa automat cu un singur click.')">
                            <template v-slot:activator="{ props }">
                              <v-btn
                                  v-bind="props"
                                  min-width="0"
                                  class="block-on-mobile"
                                  color="secondary"
                                  size="x-large"
                                  flat
                                  rounded="0"
                                  prepend-icon="mdi-content-duplicate"
                                  @click="handleAutoCompleteAddress(index)"
                                  :disabled="index === 0"
                              >
                                {{ translation(translationList.complete_action, 'Completează') }}
                              </v-btn>
                            </template>
                          </v-tooltip>
                        </v-col>
                      </v-row>

                      <v-row class="d-flex align-center">
                        <v-col cols="12" sm="12" md="6">
                          <address-county-select v-model="travelUser.address.county"
                                                 :address-index="index"
                                                 @validation-address-status="handleValidationTravelUserStatus">
                          </address-county-select>
                        </v-col>
                        <v-col cols="12" sm="12" md="6">
                          <address-city-select v-model="travelUser.address.city"
                                               :county-id="travelUser.address.county?.id"
                                               :address-index="index"
                                               @validation-address-status="handleValidationTravelUserStatus">
                          </address-city-select>
                        </v-col>
                      </v-row>
                      <v-row class="d-flex align-center">
                        <v-col cols="12" sm="12" md="4">
                          <address-street v-model="travelUser.address.street"
                                          :address-index="index"
                                          @validation-address-status="handleValidationTravelUserStatus">
                          </address-street>
                        </v-col>
                        <v-col cols="12" sm="12" md="4">
                          <address-street-number v-model="travelUser.address.street_number"
                                                 :address-index="index"
                                                 @validation-address-status="handleValidationTravelUserStatus">
                          </address-street-number>
                        </v-col>
                        <v-col cols="12" sm="12" md="4">
                          <address-postal-code v-model="travelUser.address.postal_code"
                                               :address-index="index"
                                               @validation-address-status="handleValidationTravelUserStatus">
                          </address-postal-code>
                        </v-col>
                      </v-row>
                      <v-row class="d-flex align-center">
                        <v-col cols="12" sm="12" md="4">
                          <address-building v-model="travelUser.address.building"
                                            :address-index="index"
                                            @validation-address-status="handleValidationTravelUserStatus">
                          </address-building>
                        </v-col>
                        <v-col cols="12" sm="12" md="4">
                          <address-entrance v-model="travelUser.address.entrance"
                                            :address-index="index"
                                            @validation-address-status="handleValidationTravelUserStatus">
                          </address-entrance>
                        </v-col>
                        <v-col cols="12" sm="12" md="4">
                          <address-apartment v-model="travelUser.address.apartment"
                                             :address-index="index"
                                             @validation-address-status="handleValidationTravelUserStatus">
                          </address-apartment>
                        </v-col>
                      </v-row>
                    </v-expansion-panel-text>
                  </v-expansion-panel>
                </v-expansion-panels>

                <v-row class="my-3">
                  <v-col cols="12">
                    <travel-gdpr-agreement v-model="travelForm.gdpr_agreement"
                                          @validation-travel-status="handleValidationTravelStatus">
                    </travel-gdpr-agreement>
                  </v-col>
                </v-row>


                <v-row class="mt-10 mb-15">
                  <v-col cols="12" class="d-flex align-center justify-center">
                    <v-btn
                        class="block-on-mobile"
                        color="primary"
                        size="x-large"
                        flat
                        rounded="0"
                        @click="validateStep(1)"
                    >
                      {{ translation(translationList.view, 'Vezi') }}
                      {{ translation(translationList.offers, 'Oferte') }}
                    </v-btn>
                  </v-col>
                </v-row>
              </div>
            </template>
            <template v-slot:item.2>
              <v-row>
                <v-col cols="12">
                  <div class="fw-400 fs-20">
                    {{
                      translation(translationList.perfect_choice_travel, 'Perfect! Iată cele mai bune oferte pentru călătoria ta. Compară si alege opțiunea dorită.')
                    }}
                  </div>
                </v-col>
              </v-row>
              <div class="mt-5" v-if="insuranceCompanies && insuranceCompanies.length">
                <v-card rounded="0">
                  <v-tabs
                      class="travel-tab"
                      v-model="tab"
                      color="tertiary"
                      bg-color="primary-light"
                      grow
                      center-active
                      show-arrows
                  >
                    <v-tab class="fw-600 text-grey" selected-class="selected" stacked rounded="0"
                           value="0">
                      <v-icon icon="mdi-shield"></v-icon>
                      10.000 Euro
                    </v-tab>
                    <v-tab class="fw-600 text-grey" selected-class="selected" stacked rounded="0"
                           value="1">
                      <v-icon icon="mdi-shield-star"></v-icon>
                      30.000 Euro
                    </v-tab>
                    <v-tab class="fw-600 text-grey" selected-class="selected" stacked rounded="0" value="2">
                      <v-icon icon="mdi-shield-crown"></v-icon>
                      50.000 Euro
                    </v-tab>
                  </v-tabs>

                  <v-card-text>
                    <v-tabs-window v-model="tab">
                      <v-tabs-window-item value="0">
                        <div v-if="offers && offers.length">
                          <div v-if="offers[amountKeyList.amount10k]?.length">
                            <div v-for="(offersFor10k, index) in offers[amountKeyList.amount10k]" :key="index">
                              <div v-if="offersFor10k">
                                <travel-offer :offer="offersFor10k"></travel-offer>
                              </div>
                            </div>
                          </div>
                          <div v-else-if="offers[amountKeyList.amount10k]?.length <= 0">
                            <v-card
                                rounded="0"
                                elevation="0"
                                height="180"
                            >
                              <v-card-text class="fw-500 fs-16">
                                <v-icon color="primary" size="x-large">mdi-information</v-icon>
                                {{
                                  translation(translationList.travel_offer_not_available_for, 'Nu există oferte disponibile pentru')
                                }} 10.000 Euro.
                              </v-card-text>
                            </v-card>
                          </div>
                        </div>
                        <div v-else>
                          <v-skeleton-loader type="image"></v-skeleton-loader>
                        </div>
                      </v-tabs-window-item>

                      <v-tabs-window-item value="1">
                        <div v-if="offers && offers.length">
                          <div v-if="offers[amountKeyList.amount30k]?.length">
                            <div v-for="(offersFor30k, index) in offers[amountKeyList.amount30k]" :key="index">
                              <div v-if="offersFor30k">
                                <travel-offer :offer="offersFor30k"></travel-offer>
                              </div>
                            </div>
                          </div>
                          <div v-else-if="offers[amountKeyList.amount30k]?.length <= 0">
                            <v-card
                                rounded="0"
                                elevation="0"
                                height="180"
                            >
                              <v-card-text class="fw-500 fs-16">
                                <v-icon color="primary" size="x-large">mdi-information</v-icon>
                                {{
                                  translation(translationList.travel_offer_not_available_for, 'Nu există oferte disponibile pentru')
                                }} 30.000 Euro.
                              </v-card-text>
                            </v-card>
                          </div>
                        </div>
                        <div v-else>
                          <v-skeleton-loader type="image"></v-skeleton-loader>
                        </div>
                      </v-tabs-window-item>

                      <v-tabs-window-item value="2">
                        <div v-if="offers && offers.length">
                          <div v-if="offers[amountKeyList.amount50k]?.length">
                            <div v-for="(offersFor50k, index) in offers[amountKeyList.amount50k]" :key="index">
                              <div v-if="offersFor50k">
                                <travel-offer :offer="offersFor50k"></travel-offer>
                              </div>
                            </div>
                          </div>
                          <div v-else-if="offers[amountKeyList.amount50k]?.length <= 0">
                            <v-card
                                rounded="0"
                                elevation="0"
                                height="180"
                            >
                              <v-card-text class="fw-500 fs-16">
                                <v-icon color="primary" size="x-large">mdi-information</v-icon>
                                {{
                                  translation(translationList.travel_offer_not_available_for, 'Nu există oferte disponibile pentru')
                                }} 50.000 Euro.
                              </v-card-text>
                            </v-card>
                          </div>
                        </div>
                        <div v-else>
                          <v-skeleton-loader type="image"></v-skeleton-loader>
                        </div>
                      </v-tabs-window-item>
                    </v-tabs-window>
                  </v-card-text>
                  <v-row class="mt-5" v-show="!isLoadingSkeleton">
                    <v-col cols="12" sm="12" md="6" class="d-flex justify-start centered-on-mobile">
                      <v-btn
                          class="block-on-mobile"
                          color="primary-light"
                          size="x-large"
                          flat
                          rounded="0"
                          @click="backToStep(1)"
                      >
                        {{ translation(translationList.step, 'Pasul') }}
                        {{ translation(translationList.previous, 'Anterior') }}
                      </v-btn>
                    </v-col>
                    <v-col v-show="false" v-if="(offers && offers.length) &&
                    (!offers[amountKeyList.amount10k]?.length <= 0 ||
                    !offers[amountKeyList.amount30k]?.length <= 0 ||
                    !offers[amountKeyList.amount50k]?.length <= 0)"
                           cols="12" sm="12" md="6" class="d-flex justify-end centered-on-mobile">
                      <v-btn
                          class="block-on-mobile"
                          color="primary"
                          size="x-large"
                          flat
                          rounded="0"
                          @click="sendOffersToUser()"
                          v-show="!isOffersMailSent "
                      >{{ translation(translationList.send_comparator_to_mail, 'Trimite comparatia pe email') }}
                      </v-btn>
                    </v-col>
                  </v-row>
                </v-card>
              </div>
            </template>
          </v-stepper>
        </v-container>
      </v-container>
    </v-container>
  </master-layout>
</template>

<style scoped>

</style>